import { type ButtonHTMLAttributes, type InputHTMLAttributes, type LabelHTMLAttributes, type ReactNode } from 'react';

export function Button({
  variant = 'primary',
  size = 'md',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'ghost' | 'danger'; size?: 'sm' | 'md' }) {
  const base =
    'inline-flex items-center justify-center gap-2 rounded-full font-medium transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed min-h-11';
  const sizes = size === 'sm' ? 'px-4 py-2 text-sm' : 'px-6 py-3 text-base';
  const variants = {
    primary: 'bg-brand text-white hover:bg-brand-600 active:bg-brand-700 active:translate-y-px',
    ghost: 'border border-ink bg-surface text-ink hover:bg-surface-2',
    danger: 'bg-danger text-white hover:opacity-90',
  } as const;
  return <button className={`${base} ${sizes} ${variants[variant]} ${className}`} {...props} />;
}

export function Label({ className = '', ...props }: LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={`mb-1 block text-sm font-medium text-ink-2 ${className}`} {...props} />;
}

export function Input({ invalid, className = '', ...props }: InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }) {
  const border = invalid ? 'border-danger' : 'border-line-strong focus:border-brand';
  return (
    <input
      className={`min-h-11 w-full rounded-md border ${border} bg-surface px-3 py-2 text-ink outline-none focus:ring-2 focus:ring-brand-100 ${className}`}
      aria-invalid={invalid || undefined}
      {...props}
    />
  );
}

export function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p role="alert" className="mt-1 text-sm text-danger">
      {message}
    </p>
  );
}

export function Card({ className = '', children }: { className?: string; children: ReactNode }) {
  return <div className={`rounded-lg border border-line bg-surface p-5 shadow-[var(--shadow-1)] ${className}`}>{children}</div>;
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded-md bg-surface-3 ${className}`} aria-hidden="true" />;
}

export function EmptyState({ title, description, action }: { title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-line-strong bg-surface-2 px-6 py-12 text-center">
      <p className="font-display text-lg font-semibold text-ink">{title}</p>
      {description && <p className="max-w-sm text-sm text-muted">{description}</p>}
      {action}
    </div>
  );
}

export function Alert({ variant = 'error', children }: { variant?: 'error' | 'success' | 'warn'; children: ReactNode }) {
  const styles = {
    error: 'bg-danger-50 border-danger text-danger',
    success: 'bg-brand-50 border-brand-100 text-brand-700',
    warn: 'bg-warn-50 border-warn-line text-ink-2',
  } as const;
  return (
    <div role={variant === 'error' ? 'alert' : 'status'} className={`rounded-md border px-4 py-3 text-sm ${styles[variant]}`}>
      {children}
    </div>
  );
}

export function Spinner({ label = 'Carregando' }: { label?: string }) {
  return (
    <span role="status" className="inline-flex items-center gap-2 text-sm text-muted">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-brand-100 border-t-brand" />
      {label}
    </span>
  );
}

export function Badge({
  tone = 'brand',
  children,
}: {
  tone?: 'brand' | 'accent' | 'muted' | 'danger';
  children: ReactNode;
}) {
  const styles = {
    brand: 'bg-brand-50 text-brand-700',
    accent: 'bg-accent-50 text-ink-2',
    muted: 'bg-surface-2 text-muted',
    danger: 'bg-danger-50 text-danger',
  } as const;
  return <span className={`rounded-full px-3 py-1 text-xs font-medium ${styles[tone]}`}>{children}</span>;
}
